import type { QueryClient } from '@tanstack/react-query';
import type { Product } from '../types';

/** Matches MarketplaceConstants.CheckoutReservationMinutes (server hold length). */
export const CHECKOUT_RESERVATION_MINUTES = 15;

const RESERVATION_QUERY_BASES = ['products', 'search', 'product', 'recommendations'] as const;

function applyReservedByMe(
    product: Product,
    productId: number,
    reservedUntilIso: string,
    holdRemainingSeconds: number
): Product {
    if (product.id !== productId) return product;
    return {
        ...product,
        isReserved: true,
        reservedByMe: true,
        reservedUntil: reservedUntilIso,
        holdRemainingSeconds,
    };
}

/**
 * Optimistically mark a product as held by the current viewer in all cached product shapes
 * (single detail, paginated lists, arrays). Used after successful createCheckout so browser
 * Back from Shopify shows "Continue checkout" without a hard refresh.
 */
export function markProductReservedByMe(
    queryClient: QueryClient,
    productId: number,
    holdMinutes: number = CHECKOUT_RESERVATION_MINUTES
): void {
    if (!Number.isFinite(productId) || productId <= 0) return;

    const holdRemainingSeconds = Math.max(0, Math.round(holdMinutes * 60));
    const reservedUntilIso = new Date(Date.now() + holdRemainingSeconds * 1000).toISOString();

    queryClient.setQueriesData({ queryKey: ['product'] }, (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        const p = old as Product;
        if (typeof p.id !== 'number') return old;
        return applyReservedByMe(p, productId, reservedUntilIso, holdRemainingSeconds);
    });

    queryClient.setQueriesData({ queryKey: ['products'] }, (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        const page = old as { data?: Product[] };
        if (!Array.isArray(page.data)) return old;
        return {
            ...page,
            data: page.data.map((p) =>
                applyReservedByMe(p, productId, reservedUntilIso, holdRemainingSeconds)
            ),
        };
    });

    queryClient.setQueriesData({ queryKey: ['search'] }, (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        const page = old as { data?: Product[] };
        if (!Array.isArray(page.data)) return old;
        return {
            ...page,
            data: page.data.map((p) =>
                applyReservedByMe(p, productId, reservedUntilIso, holdRemainingSeconds)
            ),
        };
    });

    queryClient.setQueriesData({ queryKey: ['recommendations'] }, (old: unknown) => {
        if (!Array.isArray(old)) return old;
        return (old as Product[]).map((p) =>
            applyReservedByMe(p, productId, reservedUntilIso, holdRemainingSeconds)
        );
    });

    // Favourites list is Product[]
    queryClient.setQueriesData({ queryKey: ['favourites'] }, (old: unknown) => {
        if (!Array.isArray(old)) return old;
        return (old as Product[]).map((p) =>
            applyReservedByMe(p, productId, reservedUntilIso, holdRemainingSeconds)
        );
    });
}

/**
 * Mark catalog/detail caches stale so the next focus/mount/bfcache restore refetches
 * live reservation overlay (IsReserved / ReservedByMe / ReservedUntil).
 */
export async function invalidateProductReservationCaches(queryClient: QueryClient): Promise<void> {
    await Promise.all(
        RESERVATION_QUERY_BASES.map((base) =>
            queryClient.invalidateQueries({
                predicate: (query) => Array.isArray(query.queryKey) && query.queryKey[0] === base,
            })
        )
    );
}

/**
 * After a successful checkout: optimistic reservedByMe + invalidate for server truth.
 * Call before redirecting to Shopify so Back restores Continue checkout immediately.
 */
export async function afterCheckoutReserve(
    queryClient: QueryClient,
    productId: number
): Promise<void> {
    markProductReservedByMe(queryClient, productId);
    await invalidateProductReservationCaches(queryClient);
}
